import { useEffect, useState } from "react";

// Click-to-select on the Files tab's own tiles — 1.0's LogPhotoSelect, which put a
// 5px green border on a picked photo and offered "Zip Selected Files" beside
// "Zip All" (Controls/LogPhotoSelect.ascx, used by the QCC and Inspection pages).
//
// 1.0 revealed a SECOND grid of photos to pick from. 2.0 does not, deliberately:
// the Files tab is already showing every file, and a second copy of the same grid
// is the duplication the house rule exists to avoid. Selection happens on the
// tiles that are already in front of you.
//
// WHY THIS DRIVES THE DOM DIRECTLY, which is not normally how we do things.
// The tiles belong to ClaimFilesTab, and the alternative is threading selection
// state, click handling and a class name through a component this experiment is
// supposed to stay out of. Reaching in through the `data-ph-tile` attribute the
// animation already reads keeps the whole feature inside this folder and keeps
// the removal a matter of deleting it. The cost is real and worth naming: this
// breaks if the Files tab stops carrying that attribute, and it silently does
// nothing rather than failing loudly. If the feature is ever made permanent, do
// it properly with state in the tab.

const TILE = "[data-ph-tile]";
const SELECTED = "ph-tile-selected";
const SELECTABLE = "ph-tile-selectable";

export default function useTileSelection(active) {
  const [selected, setSelected] = useState(() => new Set());

  const paint = (next) => {
    for (const el of document.querySelectorAll(TILE)) {
      el.classList.toggle(SELECTED, next.has(String(el.getAttribute("data-ph-tile"))));
    }
    setSelected(next);
  };

  // Add a whole group, or take it back out if it is already in. TOGGLE rather
  // than replace, so the picker can be used to build a selection out of several
  // sections instead of only ever choosing one of them.
  const toggleGroup = (ids) => {
    const keys = ids.map(String);
    const next = new Set(selected);
    if (keys.every((k) => next.has(k))) for (const k of keys) next.delete(k);
    else for (const k of keys) next.add(k);
    paint(next);
  };

  useEffect(() => {
    const tiles = () => Array.from(document.querySelectorAll(TILE));

    if (!active) {
      for (const el of tiles()) el.classList.remove(SELECTED, SELECTABLE);
      setSelected(new Set());
      return undefined;
    }

    for (const el of tiles()) el.classList.add(SELECTABLE);

    // CAPTURE phase: an image tile already has its own click handler that opens
    // the lightbox, and picking a photo must not also open it. Capturing lets this
    // run first and stop the event before it reaches the tab's handler.
    const onClick = (event) => {
      const tile = event.target.closest?.(TILE);
      if (!tile) return;
      event.preventDefault();
      event.stopPropagation();
      const id = String(tile.getAttribute("data-ph-tile"));
      setSelected((previous) => {
        const next = new Set(previous);
        if (next.has(id)) {
          next.delete(id);
          tile.classList.remove(SELECTED);
        } else {
          next.add(id);
          tile.classList.add(SELECTED);
        }
        return next;
      });
    };

    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      for (const el of tiles()) el.classList.remove(SELECTED, SELECTABLE);
    };
  }, [active]);

  return [selected, toggleGroup];
}
